import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { stringify } from "query-string"
import * as PropTypes from "prop-types"
import CarsList from "components/CarsList"
import { shallowEqual, useSelector } from "react-redux"
import { useRouter } from "next/router"
import { forceCheck } from "react-lazyload"
import filterCars from "helpers/filterCars"
import useDidUpdate from "hooks/useDidUpdate"
import dynamic from "next/dynamic"
import indexOf from "lodash/indexOf"
import { RentingContext } from "../RentingContext"

const INITIAL_AMOUNT = 30
const LAZYLOAD_OFFSET = "250px"

const isServer = typeof window === "undefined"
let supportsIO = !isServer && "IntersectionObserver" in window

if (!isServer && !supportsIO) {
  import("intersection-observer").then(() => {
    console.debug("Loaded IntersectionObserver polyfill")
    IntersectionObserver.prototype.USE_MUTATION_OBSERVER = false
    supportsIO = true
  })
}

const NoCars = dynamic(() => import("./NoCars"))

export default function Stock({ setHasCars, hasFilter, cars, ignoreQuery }) {
  const { page, withoutTaxes } = useContext(RentingContext)
  const { filter } = useSelector(mapStateToProps, shallowEqual)
  const [filteredCars, setFilteredCars] = useState(() => filterCars(cars, filter, hasFilter))
  const router = useRouter()
  const { asPath, push } = router
  const [showAll, setShowAll] = useState(false)
  const [isDoneRendering, setIsDoneRendering] = useState(false)

  const visibleCars = showAll ? filteredCars : filteredCars.slice(0, INITIAL_AMOUNT)
  const hasMore = !showAll && filteredCars.length > INITIAL_AMOUNT

  useDidUpdate(() => {
    const newList = filterCars(cars, filter, hasFilter)

    setFilteredCars(newList)
    setHasCars(newList?.length > 0)
    setShowAll(false)

    if (ignoreQuery) return

    const query = stringify(filter, { arrayFormat: "comma", skipNull: true, skipEmptyString: true })
    const basePath = (asPath || "").split("?")[0]
    const newUrl = `${basePath}${query ? `?${query}` : ""}`
    push(newUrl, undefined, { shallow: true })
    forceCheck()
  }, [asPath, cars, filter, hasFilter, ignoreQuery, setHasCars])

  const handleLoadMore = useCallback(() => {
    setShowAll(true)
  }, [])

  const onIntersection = useCallback(
    (entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.parentNode) {
            const idx = indexOf(entry.target.parentNode.children, entry.target)
            handleLoadMore(idx)
          }

          obs.unobserve(entry.target)
        }
      })
    },
    [handleLoadMore]
  )

  const observer = useMemo(
    () => supportsIO && new IntersectionObserver(onIntersection, { rootMargin: `${LAZYLOAD_OFFSET} 0px` }),
    [onIntersection, supportsIO]
  )

  useEffect(() => {
    if (!observer) return undefined

    const els = [...document.querySelectorAll(`.car-list > :nth-child(n + ${INITIAL_AMOUNT + 1})`)]
    els.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [filteredCars, observer, isDoneRendering])

  const updateParent = useCallback(() => setIsDoneRendering(current => !current), [])

  return (
    <LazyProvider value={updateParent}>
      <CarsList
        pageName={page}
        cars={visibleCars}
        withoutTaxes={withoutTaxes}
        CustomNoCars={NoCars}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
      />
    </LazyProvider>
  )
}

const mapStateToProps = state => {
  const { carsReducer } = state
  const { filter } = carsReducer

  return { filter }
}

export const LazyContext = createContext({})
function LazyProvider(props) {
  return <LazyContext.Provider {...props} />
}

Stock.defaultProps = {
  ignoreQuery: false
}

Stock.propTypes = {
  setHasCars: PropTypes.func.isRequired,
  hasFilter: PropTypes.bool.isRequired,
  cars: PropTypes.array.isRequired,
  ignoreQuery: PropTypes.bool
}
